import { WarningIcon } from "@phosphor-icons/react/Warning";
import moment from "moment";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Heading } from "@freightsimple/ui-core/Typography/Heading";
import { Icon } from "@freightsimple/ui-core/Icons/Icon";
import { Link } from "@freightsimple/ui-core/Typography/Link";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import type { ErrorMessageType } from "@freightsimple/ui-core/FormLayout/FormItem";
import type { BankAccount } from "@freightsimple/generated-dashboard-openapi-client/models/BankAccount";
import type { BookingFieldViolation } from "@freightsimple/generated-dashboard-openapi-client/models/BookingFieldViolation";
import type { Broker } from "@freightsimple/generated-dashboard-openapi-client/models/Broker";
import type { CreditCard } from "@freightsimple/generated-dashboard-openapi-client/models/CreditCard";
import type { PreBookingShipment } from "@freightsimple/generated-dashboard-openapi-client/models/PreBookingShipment";
import type { SavedBroker } from "@freightsimple/generated-dashboard-openapi-client/models/SavedBroker";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";
import { BookingSuspendedReason } from "@freightsimple/generated-dashboard-openapi-client/models/BookingSuspendedReason";
import { LocationType } from "@freightsimple/generated-dashboard-openapi-client/models/LocationType";
import { isAmazonDistributionWarehouseBrand } from "@freightsimple/shared/Helpers/isAmazonDistributionWarehouseBrand";
import type { UUID } from "@/Shared/types";
import { isCreditCardExpired } from "@/Helpers/creditCardExpiry";
import { describeHours } from "@/Helpers/describeHours";
import { priceBeforeTax } from "@/Helpers/priceBeforeTax";
import { useCoupons } from "@/Shared/Coupons/Hooks/useCoupons";
import { ChangePickupDatePreBookingDialog } from "@/Features/ChangePickupDate/Exports/Dialogs/ChangePickupDatePreBookingDialog";
import { BookShipmentBookingChargeSummary } from "../CustomComponents/BookShipmentBookingChargeSummary";
import { BookShipmentBookingRefusedBanner } from "../CustomComponents/BookShipmentBookingRefusedBanner";
import { BookShipmentReviewFrame } from "../CustomComponents/Internal/bookShipmentReviewFrame";
import { BookShipmentReviewIntroCopy } from "../CustomComponents/Internal/bookShipmentReviewIntroCopy";
import { BookShipmentChangePaymentMethodDialog } from "../Dialogs/BookShipmentChangePaymentMethodDialog";
import { bookShipmentReviewDeadlineError } from "../Helpers/bookShipmentReviewDeadlineError";
import { useBookShipmentChangeBrokerDialog } from "../Hooks/useBookShipmentChangeBrokerDialog";
import {
  loadFromShipmentToModify,
  setPickupDate,
} from "@/Slices/bookShipmentSlice";
import { BookShipmentReviewSummaryPanel } from "./bookShipmentReviewSummaryPanel";

const FinishButton = {
  FinishBooking: "Finish Booking!",
  ChangePickupDate: "Change Pickup Date",
  newQuotes: "Get new quotes for this shipment",
} as const;
type FinishButton = (typeof FinishButton)[keyof typeof FinishButton];

interface BookShipmentReviewPanelProps {
  onBookShipment: () => void;
  shipment: PreBookingShipment;
  savedLocations: Array<SavedLocation>;
  savedBrokers: Array<SavedBroker>;
  savedBrokerId: UUID | undefined;
  cards: Array<CreditCard>;
  bankAccounts: Array<BankAccount>;
  selectedStripePaymentMethodId: string | undefined;
  onCreditCardChanged: (_: string) => void;
  onBrokerChanged: (_: Broker) => void;
  onModifyDetails: () => void;
  onModifyPickupAddress: () => void;
  onModifyDeliveryAddress: () => void;
  onSelectAnotherQuote: () => void;
  bookingInProgress: boolean;
  /** Why the server refused the last Book attempt, per field. */
  violations?: BookingFieldViolation[] | undefined;
}

// oxlint-disable-next-line complexity -- grandfathered, see ENG-7672
export function BookShipmentReviewPanel(props: BookShipmentReviewPanelProps) {
  const { selectedQuote } = props.shipment;
  const { shipment, savedLocations } = props;
  const [changePaymentMethodOpen, setChangePaymentMethodOpen] = useState(false);
  const changeBrokerDialog = useBookShipmentChangeBrokerDialog();
  const [changePickupDateOpen, setChangePickupDateOpen] = useState(false);
  const dispatch = useDispatch();
  const { discountQuote } = useCoupons();

  const [showFinishBookingButton, setShowFinishBookingButton] = useState(true);

  const [finishButton, setFinishButton] = useState<FinishButton>(
    FinishButton.FinishBooking,
  );

  if (selectedQuote === undefined) {
    throw new Error("selectedQuote is undefined");
  }

  const discountedQuote = discountQuote(selectedQuote);

  const totalPrice = discountedQuote.price;

  const deliveryHours = describeHours(shipment.deliveryLocation.hours);

  const paymentTermsDays = shipment.paymentTermsDays;

  const navigate = useNavigate();

  const selectedCard = shipment.stripePaymentMethodId
    ? props.cards.find(
        (card) => card.stripePaymentMethodId === shipment.stripePaymentMethodId,
      )
    : undefined;

  const selectedBankAccount = shipment.stripePaymentMethodId
    ? props.bankAccounts.find(
        (bankAccount) =>
          bankAccount.stripePaymentMethodId === shipment.stripePaymentMethodId,
      )
    : undefined;

  const noSelectedPaymentMethod =
    selectedCard === undefined &&
    selectedBankAccount === undefined &&
    paymentTermsDays === 0;

  const onChangePaymentMethod =
    props.cards.length > 0 || props.bankAccounts.length > 0
      ? async function () {
          setChangePaymentMethodOpen(true);
        }
      : undefined;

  const onChangeBroker = async function () {
    const broker = props.shipment.broker;

    if (broker === undefined) {
      throw Error("broker is undefined - should not happen");
    }

    await changeBrokerDialog.show(
      props.savedBrokerId,
      broker,
      props.savedBrokers,
      props.onBrokerChanged,
    );
  };

  function changePickupDate() {
    setChangePickupDateOpen(true);
  }

  function isSameDayPickup() {
    const today = moment().startOf("day");
    const pickupDate = moment(props.shipment.pickupDate);
    return pickupDate.isSame(today, "day");
  }

  function isPickupInThePast() {
    const today = moment().startOf("day");
    const pickupDate = moment(props.shipment.pickupDate);
    return pickupDate.isBefore(today, "day");
  }

  // oxlint-disable-next-line complexity, sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
  function errorMessageForFinishBookingButton(): ErrorMessageType {
    const selectedQuote = props.shipment.selectedQuote;

    // The booking gate refuses an Amazon delivery without both ids; say so here,
    // with where to fix it, instead of letting the click land on the 400.
    const delivery = props.shipment.deliveryLocation;
    if (
      delivery.locationType === LocationType.DistributionWarehouse &&
      isAmazonDistributionWarehouseBrand(delivery.distributionWarehouseBrand) &&
      (!props.shipment.deliveryAmazonReferenceId ||
        !props.shipment.deliveryAmazonShipmentId)
    ) {
      return "Add the Amazon Reference ID and Shipment ID on the delivery address step before booking";
    }

    if (selectedQuote === undefined) {
      return "No quote is selected";
    }

    const selectedQuoteCreatedAt = selectedQuote.createdAt;

    if (selectedQuoteCreatedAt === undefined) {
      return "Not known when quote was created";
    }

    if (
      moment(selectedQuoteCreatedAt).isBefore(moment().subtract(30, "days"))
    ) {
      if (showFinishBookingButton) {
        setFinishButton(FinishButton.newQuotes);
        setShowFinishBookingButton(false);
      }

      return "Quote expired (more than 30 days old)";
    }

    if (isPickupInThePast()) {
      if (showFinishBookingButton) {
        setFinishButton(FinishButton.ChangePickupDate);
        setShowFinishBookingButton(false);
      }

      return "The selected pickup date is in the past. Please modify the pickup date";
    }

    // The address screen's rule, which a pickup-date change on the quotes
    // screen can invalidate without the customer ever seeing that screen again.
    const deadlineError = bookShipmentReviewDeadlineError(props.shipment);
    if (deadlineError !== undefined) {
      return deadlineError;
    }

    if (props.shipment.isBookingSuspended) {
      if (
        props.shipment.bookingSuspendedReason ===
        BookingSuspendedReason.OverdueInvoices
      ) {
        return "Booking is currently suspended. There are overdue invoices.";
      }

      if (
        props.shipment.bookingSuspendedReason ===
        BookingSuspendedReason.AlreadyOverCreditLimit
      ) {
        return "Booking is currently suspended. You are over the credit limit.";
      }

      if (
        props.shipment.bookingSuspendedReason ===
        BookingSuspendedReason.WillBeTooFarBeyondCreditLimit
      ) {
        return "Booking is currently suspended. This will go too far beyond your credit limit.";
      }

      if (
        props.shipment.bookingSuspendedReason ===
        BookingSuspendedReason.NeedsAccountVerification
      ) {
        return "Your account requires verification. Please contact us.";
      }

      if (
        props.shipment.bookingSuspendedReason ===
        BookingSuspendedReason.AccountOnHold
      ) {
        return "Booking is currently unavailable for your account. Please contact support.";
      }
    }

    if (noSelectedPaymentMethod) {
      return "No payment method is selected for this shipment";
    }

    // A resumed draft can carry a card that expired since it was selected —
    // the payment screen's guard never re-runs on this path (ENG-4368).
    // Skip for customers on credit — they pay by EFT and don't need a valid card to book.
    if (
      paymentTermsDays === 0 &&
      selectedCard !== undefined &&
      isCreditCardExpired(selectedCard.expiry)
    ) {
      return "The selected credit card has expired. Please change the payment method.";
    }

    return undefined;
  }

  const errorMessage = errorMessageForFinishBookingButton();

  const handleModifyDetails = () => {
    dispatch(loadFromShipmentToModify({ shipment, savedLocations }));
    navigate("/book/details");
  };

  return (
    <BookShipmentReviewFrame>
      <ChangePickupDatePreBookingDialog
        open={changePickupDateOpen}
        onOpenChange={setChangePickupDateOpen}
        shipment={shipment}
        onChanged={(newPickupDate) => dispatch(setPickupDate(newPickupDate))}
      />
      {changeBrokerDialog.dialogs}
      <BookShipmentChangePaymentMethodDialog
        open={changePaymentMethodOpen}
        onOpenChange={setChangePaymentMethodOpen}
        selectedStripePaymentMethodId={
          selectedCard?.stripePaymentMethodId ||
          selectedBankAccount?.stripePaymentMethodId
        }
        cards={props.cards}
        bankAccounts={props.bankAccounts}
        onChanged={(newStripePaymentMethodId) =>
          props.onCreditCardChanged(newStripePaymentMethodId)
        }
      />
      <Stack gap={16} align="start">
        <Stack gap={8} align="start">
          <Stack gap={4} align="start">
            <Stack gap={2} align="start">
              {/* Replica of the legacy Cargo Heading0 (32px bold). */}
              <Heading level={1}>Review and Book</Heading>
              {isSameDayPickup() && (
                <T size="base" color="secondary">
                  <Icon
                    icon={WarningIcon}
                    size="lg"
                    weight="fill"
                    color="text-gold"
                    inline
                  />{" "}
                  You are booking this for <strong>today</strong>. If the
                  carrier is unable to pick it up today, it will be rescheduled
                  for the next business day.
                </T>
              )}
              <BookShipmentReviewIntroCopy />
            </Stack>
            <BookShipmentBookingChargeSummary
              priceBeforeTax={priceBeforeTax(
                totalPrice,
                discountedQuote.taxAmount,
              )}
              taxAmount={discountedQuote.taxAmount}
              total={totalPrice}
              currency={discountedQuote.currency}
              paymentTermsDays={paymentTermsDays}
              hasSelectedCard={selectedCard !== undefined}
              hasSelectedBankAccount={selectedBankAccount !== undefined}
            />
          </Stack>
          <Stack gap={4} align="start">
            {props.violations && (
              <BookShipmentBookingRefusedBanner violations={props.violations} />
            )}
            {errorMessage && (
              /* Replica of the legacy Cargo ErrorMessage (18px medium red). */
              <T size="lg" weight="medium" color="red">
                {errorMessage}
              </T>
            )}
            {showFinishBookingButton ? (
              <Button
                primary
                data-testid="book-shipment-button"
                size="large"
                onClick={props.onBookShipment}
                disabled={errorMessage !== undefined || props.bookingInProgress}
                disabledMessage="Resolve the form errors above before booking"
              >
                {FinishButton.FinishBooking}
              </Button>
            ) : (
              <Button primary size="large" onClick={handleModifyDetails}>
                {finishButton === FinishButton.ChangePickupDate
                  ? FinishButton.ChangePickupDate
                  : finishButton === FinishButton.newQuotes
                    ? FinishButton.newQuotes
                    : FinishButton.FinishBooking}
              </Button>
            )}
            {/* Replica of the legacy Cargo Legalese (14px light text). */}
            <T size="sm" color="muted">
              By booking, you accept FreightSimple’s{" "}
              {/* `Link`, not `Button size="link"` — this sits inside a
                  sentence, so it has to inherit the type and wrap with the
                  copy rather than carry its own bold size. */}
              <Link
                external
                href="https://www.freightsimple.com/terms-of-service"
              >
                Terms and Conditions
              </Link>
            </T>
          </Stack>
        </Stack>
        <BookShipmentReviewSummaryPanel
          shipment={shipment}
          selectedQuote={selectedQuote}
          discountedQuote={discountedQuote}
          deliveryHours={deliveryHours}
          selectedCard={selectedCard}
          selectedBankAccount={selectedBankAccount}
          paymentTermsDays={paymentTermsDays}
          onChangePaymentMethod={onChangePaymentMethod}
          onChangeBroker={onChangeBroker}
          onChangePickupDate={changePickupDate}
          onModifyDetails={props.onModifyDetails}
          onModifyPickupAddress={props.onModifyPickupAddress}
          onModifyDeliveryAddress={props.onModifyDeliveryAddress}
          onSelectAnotherQuote={props.onSelectAnotherQuote}
        />
      </Stack>
    </BookShipmentReviewFrame>
  );
}
